interface SingleTaskWorkerApi<TInput, TResult> {
    acquire: () => void;
    release: () => void;
    postTask: (data: TInput) => Promise<TResult>;
}

export function createSingleTaskWorker<TInput, TResult>(
    workerFactory: () => Worker,
    workerName: string
): SingleTaskWorkerApi<TInput, TResult> {
    let workerInstance: Worker | undefined;
    let refCount = 0;
    let pendingTask: {
        resolve: (value: TResult) => void;
        reject: (reason: unknown) => void;
    } | null = null;

    function getOrCreateWorker(): Worker {
        if (!workerInstance) {
            workerInstance = workerFactory();

            workerInstance.addEventListener('message', (e: MessageEvent) => {
                if (pendingTask) {
                    const task = pendingTask;
                    pendingTask = null;
                    task.resolve(e.data as TResult);
                }
            });

            workerInstance.addEventListener('error', (err: ErrorEvent) => {
                if (pendingTask) {
                    const task = pendingTask;
                    pendingTask = null;
                    task.reject(err);
                }
            });
        }
        return workerInstance;
    }

    return {
        acquire(): void {
            refCount++;
            getOrCreateWorker();
        },

        release(): void {
            refCount--;
            if (refCount <= 0) {
                workerInstance?.terminate();
                workerInstance = undefined;
                refCount = 0;
                if (pendingTask) {
                    pendingTask.reject(new DOMException(`${workerName}: worker terminated`, 'AbortError'));
                    pendingTask = null;
                }
            }
        },

        postTask(data: TInput): Promise<TResult> {
            return new Promise<TResult>((resolve, reject) => {
                if (pendingTask) {
                    pendingTask.reject(
                        new DOMException(`${workerName}: superseded by newer request`, 'AbortError')
                    );
                }
                pendingTask = { resolve, reject };
                getOrCreateWorker().postMessage(data);
            });
        }
    };
}

interface ManagedWorkerApi {
    acquire: () => Worker;
    release: () => void;
}

export function createManagedWorker(workerFactory: () => Worker): ManagedWorkerApi {
    let workerInstance: Worker | undefined;
    let refCount = 0;

    return {
        acquire(): Worker {
            if (!workerInstance) {
                workerInstance = workerFactory();
            }
            refCount++;
            return workerInstance;
        },

        release(): void {
            refCount--;
            if (refCount <= 0) {
                workerInstance?.terminate();
                workerInstance = undefined;
                refCount = 0;
            }
        }
    };
}
